import { cx } from "@emotion/css";
import { RefreshCw } from "lucide-react";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import ReactCardFlip from "react-card-flip";
import { useTranslation } from "react-i18next";
import type { SwipeEventData } from "react-swipeable";
import { Swipeable } from "src/components/Default/Swipeable";
import { DelayedFlashcardCardSkeleton } from "src/components/NewComponents/Skeletons/FlashcardContentSkeleton";
import { htmlFormatter } from "src/formatters/htmlFormatter";
import isMobile from "src/helpers/isMobile";

interface FlashcardContent {
	front: string;
	back: string;
}

export type ExitDirection = "left" | "right" | "up" | "down";
export type ExitColor = "red" | "green" | "orange" | "blue";

export interface FlashcardShortcut {
	key: string;
	direction: ExitDirection;
	color: ExitColor;
	label: string;
	action: () => void;
}

export type AnimateExitFn = (
	direction: ExitDirection,
	color: ExitColor,
	action: () => void,
) => void;

interface FlashcardProps {
	flashcard: FlashcardContent;
	renderController: (animateExit: AnimateExitFn) => ReactNode;
	shortcuts?: FlashcardShortcut[];
	isLoadingNext?: boolean;
}

const EXIT_ANIMATION_MS = 220;

const KEY_LABELS: Record<string, string> = {
	ArrowLeft: "←",
	ArrowRight: "→",
	ArrowUp: "↑",
	ArrowDown: "↓",
};

const COLOR_VAR_BY_EXIT_COLOR: Record<ExitColor, string> = {
	red: "var(--red)",
	green: "var(--green)",
	orange: "var(--orange)",
	blue: "var(--primaryBlue)",
};

export function Flashcard({
	flashcard,
	renderController,
	shortcuts,
	isLoadingNext = false,
}: FlashcardProps) {
	const { t } = useTranslation();
	const [isFlipped, setIsFlipped] = useState(false);
	const [exitState, setExitState] = useState<{
		direction: ExitDirection;
		color: ExitColor;
	} | null>(null);
	const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const willExitRef = useRef(false);
	const lastCardHeightRef = useRef<number | null>(null);

	useLayoutEffect(() => {
		if (!isLoadingNext && wrapperRef.current) {
			lastCardHeightRef.current = wrapperRef.current.offsetHeight;
		}
	});

	useLayoutEffect(() => {
		if (isLoadingNext && wrapperRef.current && lastCardHeightRef.current) {
			wrapperRef.current.style.minHeight = `${lastCardHeightRef.current}px`;
		}
	}, [isLoadingNext]);

	const toggleIsFlipped = () => {
		if (exitState) return;
		setIsFlipped((prev) => !prev);
	};

	const animateExit = useCallback<AnimateExitFn>(
		(direction, color, action) => {
			if (exitState) return;
			if (wrapperRef.current) {
				wrapperRef.current.style.transition = "";
				wrapperRef.current.style.transform = "";
				wrapperRef.current.style.opacity = "";
				wrapperRef.current.style.setProperty(
					"--swipe-border-color",
					COLOR_VAR_BY_EXIT_COLOR[color],
				);
			}
			setExitState({ direction, color });
			exitTimeoutRef.current = setTimeout(() => {
				action();
			}, EXIT_ANIMATION_MS);
		},
		[exitState],
	);

	const lockedAxisRef = useRef<"horizontal" | "vertical" | null>(null);

	const handleSwiping = useCallback(
		(e: SwipeEventData) => {
			if (exitState || !wrapperRef.current) return;

			const hasHorizontal =
				shortcuts?.some(
					(s) => s.direction === "left" || s.direction === "right",
				) ?? false;
			const hasVertical =
				shortcuts?.some(
					(s) => s.direction === "up" || s.direction === "down",
				) ?? false;

			if (e.first || lockedAxisRef.current === null) {
				const initialIsHorizontal = Math.abs(e.deltaX) >= Math.abs(e.deltaY);
				lockedAxisRef.current = initialIsHorizontal ? "horizontal" : "vertical";
			}

			const allowX = lockedAxisRef.current === "horizontal" && hasHorizontal;
			const allowY = lockedAxisRef.current === "vertical" && hasVertical;
			if (!allowX && !allowY) return;

			const rawDx = allowX ? e.deltaX : 0;
			const rawDy = allowY ? e.deltaY : 0;
			const dx = Math.max(-200, Math.min(200, rawDx));
			const dy = Math.max(-200, Math.min(200, rawDy));
			const distance = Math.max(Math.abs(dx), Math.abs(dy));
			const el = wrapperRef.current;
			el.style.transition = "none";
			el.style.transform = `translate(${dx}px, ${dy}px) rotate(${dx * 0.06}deg)`;
			el.style.opacity = String(Math.max(0.4, 1 - distance / 280));

			let direction: ExitDirection | null = null;
			if (allowX) {
				direction = rawDx >= 0 ? "right" : "left";
			} else if (allowY) {
				direction = rawDy >= 0 ? "down" : "up";
			}
			const matchingShortcut = direction
				? shortcuts?.find((s) => s.direction === direction)
				: null;
			if (matchingShortcut) {
				el.style.setProperty(
					"--swipe-border-color",
					COLOR_VAR_BY_EXIT_COLOR[matchingShortcut.color],
				);
			} else {
				el.style.removeProperty("--swipe-border-color");
			}
		},
		[exitState, shortcuts],
	);

	const handleSwiped = useCallback(() => {
		lockedAxisRef.current = null;
		if (!wrapperRef.current) return;
		const el = wrapperRef.current;
		el.style.transition = "";
		el.style.transform = "";
		el.style.opacity = "";
		if (!willExitRef.current) {
			el.style.removeProperty("--swipe-border-color");
		}
		willExitRef.current = false;
	}, []);

	useEffect(() => {
		return () => {
			if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		const isEditableTarget = (target: EventTarget | null) => {
			if (!(target instanceof HTMLElement)) return false;
			const tag = target.tagName;
			return (
				tag === "INPUT" ||
				tag === "TEXTAREA" ||
				tag === "SELECT" ||
				target.isContentEditable
			);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.ctrlKey || event.metaKey || event.altKey) return;
			if (isEditableTarget(event.target)) return;
			if (exitState) return;

			if (event.code === "Space" || event.key === "Enter") {
				event.preventDefault();
				setIsFlipped((prev) => !prev);
				return;
			}

			const match = shortcuts?.find((s) => s.key === event.key);
			if (match) {
				event.preventDefault();
				animateExit(match.direction, match.color, match.action);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [animateExit, exitState, shortcuts]);

	const shortcutByDirection: Partial<Record<ExitDirection, FlashcardShortcut>> =
		{};
	shortcuts?.forEach((s) => {
		shortcutByDirection[s.direction] = s;
	});
	const swipeEnabled = isMobile() && (shortcuts?.length ?? 0) > 0;

	const exitClass = exitState ? `is-exiting-${exitState.direction}` : undefined;
	const exitShortcut = exitState
		? shortcutByDirection[exitState.direction]
		: null;
	const frontTitle = t("decks.front", "Przód");
	const backTitle = t("decks.back", "Tył");

	const renderContent = (text: string) =>
		exitShortcut ? (
			<span className="flashcard-app-flashcard--exit-label">
				{exitShortcut.label}
			</span>
		) : (
			htmlFormatter(text)
		);

	const renderFace = (
		title: string,
		flipLabel: string,
		testId: string,
		content: ReactNode,
	) => (
		<div
			className="flashcard-app-flashcard"
			onClick={toggleIsFlipped}
			data-test-id={testId}
		>
			<div className="flashcard-app-flashcard--header">
				<span className="flashcard-app-flashcard--title">{title}</span>
				<button type="button" className="flashcard-app-flashcard--flip">
					<RefreshCw className="icon-super-small" />
					{flipLabel}
				</button>
			</div>
			<div className="flashcard-app-flashcard--content">{content}</div>
		</div>
	);

	const renderCard = () => {
		if (isLoadingNext) {
			return (
				<div
					ref={wrapperRef}
					className={cx(
						"flashcard-app-flashcard-wrapper",
						"flashcard-app-flashcard-wrapper--loading",
					)}
				>
					<DelayedFlashcardCardSkeleton />
				</div>
			);
		}

		return (
			<div
				ref={wrapperRef}
				className={cx("flashcard-app-flashcard-wrapper", exitClass)}
			>
				<ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal">
					{renderFace(
						frontTitle,
						t("decks.shortcuts.flip", "Obróć"),
						"flashcard-flip-button",
						renderContent(flashcard.front),
					)}
					{renderFace(
						backTitle,
						t("decks.showFront", "Pokaż przód"),
						"flashcard-back-content",
						<>
							{!exitShortcut && (
								<div className="flashcard-app-flashcard--question">
									{htmlFormatter(flashcard.front)}
								</div>
							)}
							<div className="flashcard-app-flashcard--answer">
								{renderContent(flashcard.back)}
							</div>
						</>,
					)}
				</ReactCardFlip>
			</div>
		);
	};

	const renderHintItem = (key: string, keyLabel: string, label: string) => (
		<span className="flashcard-app--hint-item" key={key}>
			<kbd className="flashcard-app--hint-key">{keyLabel}</kbd>
			{label}
		</span>
	);

	const renderHint = () => {
		if (!shortcuts || shortcuts.length === 0 || isMobile()) return null;

		return (
			<div className="flashcard-app--hint">
				{renderHintItem(
					"flip",
					t("decks.shortcuts.spaceKey", "Spacja"),
					t("decks.shortcuts.flip", "Obróć"),
				)}
				{shortcuts.map((s) =>
					renderHintItem(s.key, KEY_LABELS[s.key] ?? s.key, s.label),
				)}
			</div>
		);
	};

	const SWIPE_COMMIT_THRESHOLD = 130;

	const makeSwipeHandler = (shortcut: FlashcardShortcut | undefined) => {
		if (!shortcut) return undefined;
		return (e: SwipeEventData) => {
			const distance =
				shortcut.direction === "left" || shortcut.direction === "right"
					? e.absX
					: e.absY;
			if (distance < SWIPE_COMMIT_THRESHOLD) return;
			willExitRef.current = true;
			animateExit(shortcut.direction, shortcut.color, shortcut.action);
		};
	};

	return (
		<div className="flashcard-app">
			<div className="flashcard-app-box">
				{swipeEnabled && !isLoadingNext ? (
					<Swipeable
						onSwipedLeft={makeSwipeHandler(shortcutByDirection.left)}
						onSwipedRight={makeSwipeHandler(shortcutByDirection.right)}
						onSwipedUp={makeSwipeHandler(shortcutByDirection.up)}
						onSwipedDown={makeSwipeHandler(shortcutByDirection.down)}
						onSwiping={handleSwiping}
						onSwiped={handleSwiped}
						delta={10}
						className="w-100"
					>
						{renderCard()}
					</Swipeable>
				) : (
					renderCard()
				)}
				{renderController(animateExit)}
				{renderHint()}
			</div>
		</div>
	);
}
